/**
 * debugCapture.js — dev-only full-session logger
 *
 * Activated when the URL contains ?debug=1 (or ?debug=<sessionId>).
 * Captures:
 *   - Calculation results (calculateSingleMonthChange, forecastPeriodMonth)
 *   - Console output (error, warn, log)
 *   - Network traffic (fetch + XHR): URL, method, status, timing, truncated body
 *
 * Buffer is capped at MAX_ENTRIES (ring buffer — oldest dropped first).
 * Auto-clears on page load so logs stay scoped to the current session.
 *
 * Usage (browser console):
 *   window.flushDebugLog()              // flush with auto session ID
 *   window.flushDebugLog('my-session')  // flush with named session
 *   window.clearDebugLog()              // clear buffer without flushing
 *   window.debugLogStatus()             // show buffer size
 */

const API_URL = import.meta.env.VITE_MAC_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Ring buffer cap — oldest entries dropped when exceeded
const MAX_ENTRIES = 500;
// Max chars to capture from network response bodies
const MAX_BODY_CHARS = 500;

// Parse ?debug param from URL
function getDebugSession() {
  try {
    const params = new URLSearchParams(window.location.search);
    const val = params.get('debug');
    if (!val) return null;
    return val === '1' ? null : val;
  } catch {
    return null;
  }
}

export const DEBUG_ENABLED = (() => {
  try {
    return new URLSearchParams(window.location.search).has('debug');
  } catch {
    return false;
  }
})();

// In-memory ring buffer
const _buffer = [];
let _sessionId = getDebugSession();

/** Push an entry, dropping the oldest if over cap. */
function _push(entry) {
  if (_buffer.length >= MAX_ENTRIES) {
    _buffer.shift(); // drop oldest
  }
  _buffer.push({ ts: Date.now(), ...entry });
}

// ─── Calculation capture ────────────────────────────────────────────────────

/**
 * Log a single calculation entry.
 * Called by wrapped calculation functions when DEBUG_ENABLED is true.
 */
export function logCalc(entry) {
  if (!DEBUG_ENABLED) return;
  _push({ kind: 'calc', ...entry });
}

// ─── Console capture ────────────────────────────────────────────────────────

function _installConsoleCapture() {
  if (!DEBUG_ENABLED || typeof window === 'undefined') return;

  const _orig = {
    error: console.error.bind(console),
    warn: console.warn.bind(console),
    log: console.log.bind(console),
  };

  const _capture = (level) => (...args) => {
    // Call original first so DevTools still shows it
    _orig[level](...args);

    // Serialize args safely
    const message = args.map(a => {
      try {
        if (typeof a === 'string') return a;
        if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ''}`;
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    }).join(' ');

    _push({ kind: 'console', level, message });
  };

  console.error = _capture('error');
  console.warn  = _capture('warn');
  console.log   = _capture('log');
}

// ─── Network capture ────────────────────────────────────────────────────────

function _installNetworkCapture() {
  if (!DEBUG_ENABLED || typeof window === 'undefined') return;

  // ── fetch ──
  const _origFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url    = typeof input === 'string' ? input : input?.url || String(input);
    const method = (init.method || 'GET').toUpperCase();
    const t0     = Date.now();

    // Skip calls to the debug endpoint itself to avoid infinite loops
    if (url.includes('/debug/')) return _origFetch(input, init);

    let status = null;
    let responseBody = null;
    try {
      const res = await _origFetch(input, init);
      status = res.status;
      // Clone so we don't consume the body
      try {
        const text = await res.clone().text();
        responseBody = text.length > MAX_BODY_CHARS ? text.slice(0, MAX_BODY_CHARS) + '…' : text;
      } catch { /* ignore */ }
      _push({ kind: 'network', transport: 'fetch', method, url, status, ms: Date.now() - t0, body: responseBody });
      return res;
    } catch (err) {
      _push({ kind: 'network', transport: 'fetch', method, url, status: 'ERR', ms: Date.now() - t0, error: err.message });
      throw err;
    }
  };

  // ── XHR ──
  const _origOpen = XMLHttpRequest.prototype.open;
  const _origSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._debugMethod = method;
    this._debugUrl    = url;
    this._debugT0     = null;
    return _origOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function(body) {
    if (this._debugUrl && !this._debugUrl.includes('/debug/')) {
      this._debugT0 = Date.now();
      this.addEventListener('loadend', () => {
        const responseBody = this.responseText
          ? (this.responseText.length > MAX_BODY_CHARS
              ? this.responseText.slice(0, MAX_BODY_CHARS) + '…'
              : this.responseText)
          : null;
        _push({
          kind: 'network',
          transport: 'xhr',
          method: this._debugMethod,
          url: this._debugUrl,
          status: this.status,
          ms: Date.now() - this._debugT0,
          body: responseBody,
        });
      });
    }
    return _origSend.call(this, body);
  };
}

// ─── Auto-clear on page load ─────────────────────────────────────────────────

function _autoClearOnLoad() {
  if (!DEBUG_ENABLED) return;
  // Use sessionStorage to detect true page loads vs React re-renders.
  // sessionStorage persists within a tab session but clears on actual refresh.
  const loadKey = `debug_capture_loaded_${_sessionId || 'default'}`;
  const alreadyLoaded = sessionStorage.getItem(loadKey);

  if (!alreadyLoaded) {
    // True page load — clear buffer and mark as loaded
    _buffer.length = 0;
    sessionStorage.setItem(loadKey, '1');
    // Tell backend to clear any existing session (best-effort)
    if (_sessionId) {
      fetch(`${API_URL}/debug/session/${encodeURIComponent(_sessionId)}`, {
        method: 'DELETE',
      }).catch(() => {});
    }
  }
  // If already loaded (React re-render/remount), do NOT clear the buffer
}

// ─── Flush / control ─────────────────────────────────────────────────────────

/**
 * Flush the buffer to the backend.
 * @param {string} [sessionId] - Optional session name.
 * @param {string} [accessToken] - JWT token.
 */
export async function flushDebugLog(sessionId, accessToken) {
  if (!DEBUG_ENABLED) {
    console.warn('[debugCapture] Debug mode not active. Load with ?debug=1');
    return;
  }

  const session = sessionId || _sessionId || String(Date.now());
  const entries = [..._buffer];

  if (entries.length === 0) {
    console.info('[debugCapture] Buffer is empty — nothing to flush.');
    return;
  }

  const headers = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  try {
    const res = await fetch(`${API_URL}/debug/log`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        session,
        entries,
        meta: {
          url: window.location.href,
          userAgent: navigator.userAgent,
          capturedAt: new Date().toISOString(),
          counts: {
            calc:    entries.filter(e => e.kind === 'calc').length,
            console: entries.filter(e => e.kind === 'console').length,
            network: entries.filter(e => e.kind === 'network').length,
          },
        },
      }),
    });

    const data = await res.json();
    if (res.ok) {
      console.info(`[debugCapture] Flushed ${entries.length} entries → session "${data.session}"`);
      console.info(`[debugCapture] Read with: redis-cli GET "debug:session:${data.session}"`);
      _buffer.length = 0;
      _sessionId = session;
    } else {
      console.error('[debugCapture] Flush failed:', data);
    }
  } catch (err) {
    console.error('[debugCapture] Network error during flush:', err);
  }
}

/** Clear the buffer without flushing. */
export function clearDebugLog() {
  _buffer.length = 0;
  console.info('[debugCapture] Buffer cleared.');
}

/** Show current buffer status. */
export function debugLogStatus() {
  const counts = {
    calc:    _buffer.filter(e => e.kind === 'calc').length,
    console: _buffer.filter(e => e.kind === 'console').length,
    network: _buffer.filter(e => e.kind === 'network').length,
    total:   _buffer.length,
  };
  console.info(`[debugCapture] Buffer: ${counts.total}/${MAX_ENTRIES} | calc:${counts.calc} console:${counts.console} network:${counts.network} | Session: ${_sessionId || '(auto)'}`);
  return { ...counts, session: _sessionId, active: DEBUG_ENABLED };
}

// ─── Bootstrap ───────────────────────────────────────────────────────────────

if (typeof window !== 'undefined' && DEBUG_ENABLED) {
  _autoClearOnLoad();
  _installConsoleCapture();
  _installNetworkCapture();

  window.flushDebugLog  = flushDebugLog;
  window.clearDebugLog  = clearDebugLog;
  window.debugLogStatus = debugLogStatus;

  // Use the original console.info (before we patched it) for the banner
  const _origInfo = Function.prototype.bind.call(console.info, console);
  _origInfo(
    `%c[BridgeFirst Debug Mode Active]%c\n` +
    `Capturing: console + network + calculations | Buffer cap: ${MAX_ENTRIES}\n` +
    `Session: ${_sessionId || '(auto-assigned on flush)'}\n` +
    `Commands: window.flushDebugLog() | window.clearDebugLog() | window.debugLogStatus()`,
    'color: #4CAF50; font-weight: bold; font-size: 13px;',
    'color: inherit;'
  );
}
